import { useEffect, useRef, useState } from "react";
import { Box, IconButton, Slider, Typography } from "@mui/material";
import { PlayArrow, Pause, GraphicEq } from "@mui/icons-material";

type Props = {
  src: string;
  /** Recorder-measured duration, used as fallback for WebM clips whose
   * metadata duration reads Infinity until they are played. */
  durationSec?: number;
};

const formatTime = (sec: number) => {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export default function VoiceNotePlayer({ src, durationSec }: Props) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(durationSec ?? 0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onMeta = () => {
      if (isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onTime = () => setCurrent(audio.currentTime);
    const onEnd = () => {
      setPlaying(false);
      setCurrent(0);
    };
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnd);
    };
  }, [src]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play();
      setPlaying(true);
    }
  };

  const seek = (value: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = value;
    setCurrent(value);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 0.5,
        borderRadius: 99,
        bgcolor: "action.hover",
        maxWidth: 320,
      }}
    >
      <audio ref={audioRef} src={src} preload="metadata" />
      <IconButton
        size="small"
        onClick={toggle}
        color="primary"
        sx={{ bgcolor: "primary.main", color: "primary.contrastText", "&:hover": { bgcolor: "primary.dark" } }}
      >
        {playing ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
      </IconButton>
      <GraphicEq fontSize="small" color="action" />
      <Slider
        size="small"
        value={Math.min(current, duration)}
        max={duration || 0.0001}
        onChange={(_, v) => seek(v as number)}
        sx={{ flex: 1, minWidth: 80 }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, textAlign: "right" }}>
        {formatTime(playing || current > 0 ? current : duration)}
      </Typography>
    </Box>
  );
}
